import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBranches } from '@/hooks/api/branches';
import { useDevices } from '@/hooks/api/devices';
import { useBranchMutations } from '@/hooks/useBranchMutations';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit2, Trash2, Loader2, MapPin } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import RegionDistrictSelect from '@/components/RegionDistrictSelect';
import { BRANCH_STATUSES, branchStatusLabel, useRegions, useAllDistricts } from '@/hooks/use-geo';

const DAYS = [
  { key: 'mon', label: 'cust_br.day_mon' },
  { key: 'tue', label: 'cust_br.day_tue' },
  { key: 'wed', label: 'cust_br.day_wed' },
  { key: 'thu', label: 'cust_br.day_thu' },
  { key: 'fri', label: 'cust_br.day_fri' },
  { key: 'sat', label: 'cust_br.day_sat' },
  { key: 'sun', label: 'cust_br.day_sun' },
];

const emptyForm = {
  name: '', address: '', phone: '', latitude: '', longitude: '',
  website: '', social_instagram: '', social_telegram: '', social_facebook: '',
  branch_code: '', region_id: null as number | null, district_id: null as number | null, status: 'active',
  is_visible_in_app: true,
  working_hours: {} as Record<string, string>,
};

const normalizeUrl = (v: string) => {
  const t = v.trim();
  if (!t) return '';
  if (/^https?:\/\//i.test(t)) return t;
  if (t.startsWith('@')) return `https://t.me/${t.slice(1)}`;
  return `https://${t}`;
};

export default function BranchesTab() {
  const { t } = useTranslation();
  const { data: branches = [], isLoading: loadingBranches } = useBranches();
  const { data: devices = [], isLoading: loadingDevices } = useDevices();
  const { data: regions = [] } = useRegions();
  const { data: allDistricts = [] } = useAllDistricts();
  const { createBranch, updateBranch, toggleBranch, deleteBranch } = useBranchMutations();

  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const closeForm = () => { setShowForm(false); setEditId(null); setForm(emptyForm); };

  const openEdit = (b: any) => {
    setEditId(b.id);
    setForm({
      name: b.name, address: b.address, phone: b.phone,
      latitude: b.latitude?.toString() || '', longitude: b.longitude?.toString() || '',
      website: b.website || '', social_instagram: b.social_instagram || '',
      social_telegram: b.social_telegram || '', social_facebook: b.social_facebook || '',
      branch_code: b.branch_code || '',
      region_id: b.region_id ?? null, district_id: b.district_id ?? null,
      status: b.status || 'active',
      is_visible_in_app: b.is_visible_in_app ?? true,
      working_hours: (b.working_hours as Record<string, string>) || {},
    });
    setShowForm(true);
  };

  const saveBranch = () => {
    const payload = {
      name: form.name.trim(),
      address: form.address.trim(),
      phone: form.phone.trim(),
      latitude: form.latitude ? parseFloat(form.latitude) : null,
      longitude: form.longitude ? parseFloat(form.longitude) : null,
      website: form.website.trim(),
      social_instagram: form.social_instagram.trim(),
      social_telegram: form.social_telegram.trim(),
      social_facebook: form.social_facebook.trim(),
      branch_code: form.branch_code.trim(),
      region_id: form.region_id,
      district_id: form.district_id,
      status: form.status,
      is_visible_in_app: form.is_visible_in_app,
      working_hours: form.working_hours,
    };
    if (editId) {
      updateBranch.mutate({ id: editId, body: payload }, { onSuccess: closeForm });
    } else {
      createBranch.mutate(payload, { onSuccess: closeForm });
    }
  };

  const geoLabel = (b: any) => {
    const region = regions.find(r => r.id === b.region_id)?.name;
    const district = allDistricts.find(d => d.id === b.district_id)?.name;
    return [region, district].filter(Boolean).join(', ');
  };

  const openBranchMap = (b: any) => {
    const lat = b.latitude || '41.2995';
    const lng = b.longitude || '69.2401';
    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
  };

  return (
    <div className="space-y-4">
      <Tabs defaultValue="branches">
        <TabsList>
          <TabsTrigger value="branches">{t('cust_br.tab_branches')}</TabsTrigger>
          <TabsTrigger value="devices">{t('cust_br.tab_devices')}</TabsTrigger>
        </TabsList>

        <TabsContent value="branches">
          <div className="flex justify-end mb-3 mt-4">
            <Button size="sm" onClick={() => setShowForm(true)}><Plus size={14} className="mr-1" /> {t('cust_br.add_branch')}</Button>
          </div>
          {loadingBranches ? (
            <p className="text-center text-muted-foreground p-8">{t('cust_common.loading')}</p>
          ) : branches.length === 0 ? (
            <p className="text-center text-muted-foreground p-8">{t('cust_br.not_found')}</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {branches.map((b: any) => (
                <div key={b.id} className="bg-card border border-border rounded-xl p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-medium text-foreground">{b.name}</p>
                        {b.branch_code && (
                          <span className="text-xs font-mono text-muted-foreground">{b.branch_code}</span>
                        )}
                      </div>
                      {geoLabel(b) && <p className="text-xs text-muted-foreground mt-1">{geoLabel(b)}</p>}
                      <p className="text-xs text-muted-foreground mt-1">{b.address}</p>
                      <p className="text-xs text-muted-foreground">{b.phone}</p>
                      {(b.website || b.social_instagram || b.social_telegram || b.social_facebook) && (
                        <div className="flex flex-wrap items-center gap-2 mt-2">
                          {[
                            { label: t('cust_br.website'), value: b.website },
                            { label: 'Instagram', value: b.social_instagram },
                            { label: 'Telegram', value: b.social_telegram },
                            { label: 'Facebook', value: b.social_facebook },
                          ].filter(l => l.value).map(l => (
                            <a
                              key={l.label}
                              href={normalizeUrl(l.value)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-xs text-primary hover:underline"
                            >
                              {l.label}
                            </a>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {(b.latitude || b.longitude) && (
                        <button onClick={() => openBranchMap(b)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground" title={t('cust_br.view_on_map')}>
                          <MapPin size={14} />
                        </button>
                      )}
                      <button onClick={() => openEdit(b)} className="p-1.5 rounded-lg hover:bg-accent text-muted-foreground">
                        <Edit2 size={14} />
                      </button>
                      <button onClick={() => setDeleteId(b.id)} className="p-1.5 rounded-lg hover:bg-destructive/10 text-destructive">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                  <div className="mt-2 flex items-center justify-between">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge className={`border-0 ${b.is_active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>
                        {b.is_active ? t('cust_br.active') : t('cust_br.inactive')}
                      </Badge>
                      <Badge variant="outline" className="text-xs">{branchStatusLabel(b.status)}</Badge>
                      {b.is_visible_in_app === false && (
                        <Badge variant="outline" className="text-xs">{t('cust_br.hidden_in_app')}</Badge>
                      )}
                    </div>
                    <Button
                      size="sm" variant="ghost" className="text-xs h-7"
                      onClick={() => toggleBranch.mutate({ id: b.id, is_active: !b.is_active })}
                    >
                      {b.is_active ? t('cust_br.disable') : t('cust_br.enable')}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="devices">
          <div className="bg-card border border-border rounded-xl overflow-hidden mt-4">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_br.col_name')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_br.col_type')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_br.col_serial')}</th>
                  <th className="text-left text-xs font-medium text-muted-foreground p-3">{t('cust_br.col_state')}</th>
                </tr>
              </thead>
              <tbody>
                {loadingDevices ? (
                  <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t('cust_common.loading')}</td></tr>
                ) : devices.length === 0 ? (
                  <tr><td colSpan={4} className="p-8 text-center text-muted-foreground">{t('cust_br.devices_not_found')}</td></tr>
                ) : (
                  devices.map((d: any) => (
                    <tr key={d.id} className="border-b border-border last:border-0">
                      <td className="p-3 text-sm text-foreground">{d.name}</td>
                      <td className="p-3 text-sm text-muted-foreground capitalize">{d.device_type}</td>
                      <td className="p-3 text-sm text-muted-foreground font-mono">{d.serial_number}</td>
                      <td className="p-3"><Badge className={`text-xs border-0 ${d.is_active ? 'bg-success/10 text-success' : 'bg-muted text-muted-foreground'}`}>{d.is_active ? t('cust_br.active') : t('cust_br.inactive')}</Badge></td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Add/Edit dialog */}
      <Dialog open={showForm} onOpenChange={v => { if (!v) closeForm(); }}>
        <DialogContent className="max-w-none w-screen h-[100dvh] max-h-[100dvh] top-0 left-0 translate-x-0 translate-y-0 rounded-none p-0 gap-0 flex flex-col overflow-hidden">
          <div className="flex flex-col h-full min-h-0 max-w-6xl mx-auto w-full">
            <DialogHeader className="shrink-0 px-6 py-5 border-b text-left">
              <DialogTitle>{editId ? t('cust_br.edit_title') : t('cust_br.new_title')}</DialogTitle>
            </DialogHeader>
            <div className="flex-1 min-h-0 overflow-y-auto px-6 py-6">
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t('cust_br.col_name')}</label>
                    <Input placeholder={t('cust_br.branch_name_ph')} value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t('cust_br.branch_code')}</label>
                    <Input placeholder="F-001" value={form.branch_code} onChange={e => setForm(f => ({ ...f, branch_code: e.target.value }))} />
                  </div>
                </div>
                <RegionDistrictSelect
                  regionId={form.region_id}
                  districtId={form.district_id}
                  onChange={next => setForm(f => ({ ...f, ...next }))}
                />
                <Input placeholder={t('cust_br.address')} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} />
                <Input placeholder={t('cust_br.phone')} value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t('cust_br.status_label')}</label>
                    <Select value={form.status} onValueChange={v => setForm(f => ({ ...f, status: v }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {BRANCH_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <div className="flex w-full items-center justify-between rounded-lg border border-border px-3 py-2">
                      <span className="text-xs text-muted-foreground">{t('cust_br.visible_in_app')}</span>
                      <Switch
                        checked={form.is_visible_in_app}
                        onCheckedChange={v => setForm(f => ({ ...f, is_visible_in_app: v }))}
                      />
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-2 block">{t('cust_br.working_hours')}</label>
                  <div className="space-y-2">
                    {DAYS.map(d => (
                      <div key={d.key} className="flex items-center gap-2">
                        <span className="text-xs text-muted-foreground w-24">{t(d.label)}</span>
                        <Input
                          className="flex-1"
                          placeholder="09:00-18:00"
                          value={form.working_hours[d.key] || ''}
                          onChange={e => setForm(f => ({ ...f, working_hours: { ...f.working_hours, [d.key]: e.target.value } }))}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t('cust_br.website')}</label>
                    <Input placeholder="https://example.uz" value={form.website} onChange={e => setForm(f => ({ ...f, website: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Instagram</label>
                    <Input placeholder={t('cust_br.handle_or_link')} value={form.social_instagram} onChange={e => setForm(f => ({ ...f, social_instagram: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Telegram</label>
                    <Input placeholder={t('cust_br.handle_or_link')} value={form.social_telegram} onChange={e => setForm(f => ({ ...f, social_telegram: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">Facebook</label>
                    <Input placeholder={t('cust_br.link')} value={form.social_facebook} onChange={e => setForm(f => ({ ...f, social_facebook: e.target.value }))} />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t('cust_br.latitude')}</label>
                    <Input placeholder="41.2995" value={form.latitude} onChange={e => setForm(f => ({ ...f, latitude: e.target.value }))} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground mb-1 block">{t('cust_br.longitude')}</label>
                    <Input placeholder="69.2401" value={form.longitude} onChange={e => setForm(f => ({ ...f, longitude: e.target.value }))} />
                  </div>
                </div>
                {(form.latitude || form.longitude) && (
                  <Button size="sm" variant="outline" onClick={() => {
                    const lat = form.latitude || '41.2995';
                    const lng = form.longitude || '69.2401';
                    window.open(`https://www.google.com/maps?q=${lat},${lng}`, '_blank');
                  }}>
                    <MapPin size={14} className="mr-1" /> {t('cust_br.open_gmaps')}
                  </Button>
                )}
              </div>
            </div>
            <DialogFooter className="shrink-0 mt-0 px-6 py-4 border-t bg-card flex flex-row flex-wrap items-center justify-between gap-3">
              <div />
              <div className="flex items-center gap-2 ml-auto">
                <Button variant="outline" onClick={closeForm}>{t('cust_br.cancel')}</Button>
                <Button onClick={saveBranch} disabled={!form.name.trim() || createBranch.isPending || updateBranch.isPending}>
                  {(createBranch.isPending || updateBranch.isPending) && <Loader2 size={14} className="mr-1 animate-spin" />}
                  {(createBranch.isPending || updateBranch.isPending) ? t('cust_br.saving') : t('cust_br.save')}
                </Button>
              </div>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation */}
      <Dialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('cust_br.delete_title')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t('cust_br.delete_confirm')}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteId(null)}>{t('cust_br.cancel')}</Button>
            <Button
              variant="destructive"
              onClick={() => deleteId && deleteBranch.mutate(deleteId, { onSuccess: () => setDeleteId(null) })}
              disabled={deleteBranch.isPending}
            >
              {deleteBranch.isPending ? t('cust_br.deleting') : t('cust_br.delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
